// src/components/settings/CredentialsTab.jsx - Updated with Twitter/X support
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { settingsAPI } from '../../services/api';
import { EyeIcon, EyeSlashIcon, KeyIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const credentialGroups = [
    {
        title: 'AI Services',
        icon: '🤖',
        credentials: [
            { key: 'GEMINI_API_KEY', label: 'Google Gemini API Key', description: 'Primary AI provider for content generation and Whisk images' },
            { key: 'OPENAI_API_KEY', label: 'OpenAI API Key', description: 'Alternative AI provider for content generation' }
        ]
    },
    {
        title: 'LinkedIn',
        icon: '💼',
        credentials: [
            { key: 'LINKEDIN_PERSONAL_ACCESS_TOKEN', label: 'Personal Access Token', description: 'For posting to your profile' },
            { key: 'LINKEDIN_PERSONAL_URN', label: 'Personal URN', description: 'Your LinkedIn profile URN' },
            { key: 'LINKEDIN_ORGANIZATION_ACCESS_TOKEN', label: 'Organization Access Token', description: 'For posting to company page' },
            { key: 'LINKEDIN_ORGANIZATION_URN', label: 'Organization URN', description: 'Your company page URN' }
        ]
    },
    {
        title: 'Twitter / X',
        icon: '🐦',
        credentials: [
            { key: 'TWITTER_API_KEY', label: 'API Key', description: 'Your Twitter app API key' },
            { key: 'TWITTER_API_SECRET', label: 'API Secret Key', description: 'Your Twitter app API secret' },
            { key: 'TWITTER_ACCESS_TOKEN', label: 'Access Token', description: 'Your account access token' },
            { key: 'TWITTER_ACCESS_TOKEN_SECRET', label: 'Access Token Secret', description: 'Your account access token secret' },
            { key: 'TWITTER_BEARER_TOKEN', label: 'Bearer Token', description: 'For API v2 operations (optional)' }
        ]
    },
    {
        title: 'BlueSky',
        icon: '🦋',
        credentials: [
            { key: 'BLUESKY_HANDLE', label: 'Bluesky Handle', description: 'Your @username.bsky.social' },
            { key: 'BLUESKY_PASSWORD', label: 'Bluesky Password', description: 'Account password or app password' },
        ]
    },
    {
        title: 'Instagram - (beta)',
        icon: '📷',
        credentials: [
            { key: 'INSTAGRAM_ACCESS_TOKEN', label: 'Instagram Access Token', description: 'Facebook Graph API token' },
            { key: 'INSTAGRAM_BUSINESS_ACCOUNT_ID', label: 'Instagram Business Account ID', description: 'Your business account ID' }
        ]
    },
    {
        title: 'Facebook - (beta)',
        icon: '👤',
        credentials: [
            { key: 'FACEBOOK_ACCESS_TOKEN', label: 'Facebook Page Access Token', description: 'Page-specific token from Meta' },
            { key: 'FACEBOOK_PAGE_ID', label: 'Facebook Page ID', description: 'Your Facebook Page ID' },
        ]
    },
    // {
    //     title: 'Other Platforms',
    //     icon: '🌐',
    //     credentials: [
    //         // { key: 'INSTAGRAM_ACCESS_TOKEN', label: 'Instagram Access Token', description: 'Facebook Graph API token' },
    //         // { key: 'INSTAGRAM_BUSINESS_ACCOUNT_ID', label: 'Instagram Business Account ID', description: 'Your business account ID' }
    //     ]
    // },
    {
        title: 'Notifications',
        icon: '🔔',
        credentials: [
            { key: 'DISCORD_WEBHOOK_URL', label: 'Discord Webhook URL', description: 'For posting notifications' },
            { key: 'WEBHOOK_URL', label: 'Custom Webhook URL', description: 'Your custom notification endpoint' }
        ]
    }
];

function CredentialsTab() {
    const [showPasswords, setShowPasswords] = useState({});
    const queryClient = useQueryClient();

    const { data: credentials, isLoading } = useQuery('credentials', settingsAPI.getCredentials);

    const updateMutation = useMutation(settingsAPI.saveCredentials, {
        onSuccess: () => {
            queryClient.invalidateQueries('credentials');
            toast.success('Credentials saved securely!');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to save credentials');
        },
    });

    const togglePasswordVisibility = (key) => {
        setShowPasswords(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const credentialsData = {};

        for (let [key, value] of formData.entries()) {
            if (value.trim()) {
                credentialsData[key] = value.trim();
            }
        }

        // console.log('Submitting credentials:', Object.keys(credentialsData));
        updateMutation.mutate(credentialsData);
    };

    const isPasswordField = (key) => {
        return key.toLowerCase().includes('password') ||
            key.toLowerCase().includes('secret') ||
            key.toLowerCase().includes('key') ||
            key.toLowerCase().includes('token');
    };

    if (isLoading) {
        return <div className="animate-pulse">Loading credentials...</div>;
    }

    // Access the correct data path
    const credentialValues = credentials?.data?.data || credentials?.data || {};

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900">API Credentials</h3>
                <p className="mt-1 text-sm text-gray-600">
                    Configure your API keys and tokens. All credentials are encrypted and stored securely.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {credentialGroups.map((group) => (
                    <div key={group.title} className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                            <span className="text-2xl mr-3">{group.icon}</span>
                            <h4 className="text-lg font-medium text-gray-900">{group.title}</h4>
                        </div>

                        {/* Twitter Setup Instructions */}
                        {group.title === 'Twitter / X' && (
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                                <h5 className="text-sm font-medium text-blue-800 mb-2">📋 Twitter API Setup Guide</h5>
                                <div className="text-sm text-blue-700 space-y-1">
                                    <p>1. Visit <a href="https://developer.twitter.com/en/portal" target="_blank" rel="noopener noreferrer" className="underline">Twitter Developer Portal</a></p>
                                    <p>2. Create a new app or use existing app</p>
                                    <p>3. Navigate to "Keys and Tokens" tab</p>
                                    <p>4. Copy API Key, API Secret, Access Token, and Access Token Secret</p>
                                    <p>5. Ensure your app has "Read and Write" permissions</p>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-4">
                            {group.credentials.map((cred) => {
                                const currentValue = credentialValues[cred.key] || '';
                                const hasValue = !!currentValue;

                                return (
                                    <div key={cred.key}>
                                        <label htmlFor={cred.key} className="block text-sm font-medium text-gray-700">
                                            {cred.label}
                                            {hasValue && (
                                                <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                                    ✓ Set
                                                </span>
                                            )}
                                            {cred.key === 'TWITTER_BEARER_TOKEN' && (
                                                <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                    Optional
                                                </span>
                                            )}
                                        </label>
                                        <div className="mt-1 relative">
                                            <input
                                                type={isPasswordField(cred.key) && !showPasswords[cred.key] ? 'password' : 'text'}
                                                name={cred.key}
                                                id={cred.key}
                                                defaultValue={currentValue}
                                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                placeholder={hasValue ? '••••••••••••••••' : `Enter your ${cred.label.toLowerCase()}`}
                                            />
                                            {isPasswordField(cred.key) && (
                                                <button
                                                    type="button"
                                                    onClick={() => togglePasswordVisibility(cred.key)}
                                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                                >
                                                    {showPasswords[cred.key] ? (
                                                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                                                    ) : (
                                                        <EyeIcon className="h-5 w-5 text-gray-400" />
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                        <p className="mt-1 text-sm text-gray-500">{cred.description}</p>

                                        {/* Debug info for each field */}
                                        <p className="mt-1 text-xs text-gray-400">
                                            Current: {hasValue ? `${currentValue.length} characters` : 'Not set'}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={updateMutation.isLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        {updateMutation.isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <KeyIcon className="h-4 w-4 mr-2" />
                                Save Credentials
                            </>
                        )}
                    </button>
                </div>
            </form>

            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <span className="text-yellow-400 text-lg">⚠️</span>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">Security Notice</h3>
                        <div className="mt-2 text-sm text-yellow-700">
                            <p>
                                {/*Your credentials are encrypted using AES-256 encryption before being stored.*/}
                                Never share your API keys or tokens with anyone.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CredentialsTab;